import { useState, useEffect } from "react";
import { format, isBefore, startOfDay, startOfToday } from "date-fns";
import { CalendarIcon } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import { cn } from "../lib/util";
import { Button } from "./ui/button";
import { Calendar } from "./ui/bookCalendar";

interface CustomDatePickerProps {
  selectedDate: string;
  onDateSelect: (date: string) => void;
  className?: string;
  error?: boolean | string;
}

export function CustomDatePicker({
  selectedDate,
  onDateSelect,
  className,
  error = false,
}: CustomDatePickerProps) {
  const [date, setDate] = useState<Date | undefined>(() => {
    if (!selectedDate) return undefined;
    // Parse the date string in local timezone to avoid timezone offset issues
    const [year, month, day] = selectedDate.split('-').map(Number);
    return new Date(year, month - 1, day);
  });
  const [open, setOpen] = useState(false);

  // Get today's date at start of day for comparison
  const today = startOfToday();

  useEffect(() => {
    if (selectedDate) {
      // Parse the date string in local timezone to avoid timezone offset issues
      const [year, month, day] = selectedDate.split('-').map(Number);
      setDate(new Date(year, month - 1, day));
    }
  }, [selectedDate]);

  const handleSelect = (newDate: Date | undefined) => {
    if (!newDate || isBefore(newDate, today)) return;
    
    setDate(newDate);
    if (newDate) {
      // Format date as YYYY-MM-DD for the input value
      const formattedDate = format(newDate, "yyyy-MM-dd");
      onDateSelect(formattedDate);
      setOpen(false);
    }
  };

  return (
    <div className={cn("w-full", className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            className={cn(
              "flex h-auto w-full items-center justify-between rounded-xl border border-[#E3C9C8] bg-white px-4 py-3 text-left text-base font-normal text-[#3a2e2c] transition-colors hover:border-[#B87775] hover:bg-white hover:text-[#3a2e2c] focus-visible:border-[#B87775] focus-visible:ring-2 focus-visible:ring-[#B87775]/50 focus-visible:ring-offset-0",
              !date && "text-[#bcaca9]",
              error && "border-[#d98c89] focus-visible:ring-[#d98c89]/50"
            )}
          >
            <span>{date ? format(date, "dd MMM yyyy") : "Select a date"}</span>
            <CalendarIcon className="h-4 w-4 text-[#B87775]" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-auto rounded-2xl border border-[#EAD7D6] p-0 shadow-[0_16px_40px_rgba(184,119,117,0.18)]"
          align="start"
        >
          <Calendar
            mode="single"
            selected={date}
            onSelect={handleSelect}
            fromDate={today} // Only allows dates from today onward
            disabled={(date) => isBefore(date, today)} // Disable dates before today
            initialFocus
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}